import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { currentUserSelector, currentUserShowSuccess } from '../../ducks/user.duck';
import { useSelector } from 'react-redux';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import {
  createCheckIn,
  fetchActivePublicMoorageCheckInForCurrentUser,
  fetchPublicMoorageCheckInHistory,
} from '../../util/api';
import { getUserBoatDetails } from '../../util/dataExtractors';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { createSlug } from '../../util/urlHelpers';
import { Modal, PrimaryButton, NamedLink, SecondaryButton, Icons } from '../../components';
import { onTriggerError, onTriggerInfo } from '../../util/toast';
import {
  getBrowserCoordinates,
  isCheckInGeofenceBypassEnabled,
  validateCheckInGeofence,
} from '../../util/publicMoorageGeofence';

import css from './PublicMoorageCheckInModal.module.css';

const durationOptions = [
  { key: '4_hours', label: '4 hrs', helper: 'Quick stop' },
  { key: 'tonight', label: 'Tonight', helper: 'Until 10 AM' },
  { key: '2_days', label: '2 days', helper: 'Weekend' },
];
const CHECK_IN_PRIVATE_DATA_KEY = 'publicMoorageCheckIn';
const CHECK_IN_HISTORY_PRIVATE_DATA_KEY = 'publicMoorageCheckIns';
const CHECK_IN_TYPE_OWN_VESSEL = 'own_vessel';
const CHECK_IN_TYPE_REPORTED_VESSEL = 'reported_vessel';

const formatCheckout = (intl, date, timeZone) => {
  if (!date) return 'N/A';
  const formattedDate = intl.formatDate(date, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
  const formattedTime = intl.formatTime(date, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  return `${formattedDate} at ${formattedTime}`;
};

const getCheckInDetails = (checkIn, listing) => {
  if (!checkIn) return null;
  const expiresAt = checkIn?.expiresAt ? new Date(checkIn.expiresAt) : null;
  return {
    location: listing?.attributes?.title || 'Unknown',
    boatName:
      checkIn?.reportedVesselName ||
      checkIn?.boatName ||
      checkIn?.boat?.name ||
      checkIn?.vessel?.name,
    expires: expiresAt,
    timeZone: checkIn?.timeZone || 'Etc/UTC',
  };
};

export const getCheckInListingsToStore = result => {
  const releasedListings = Array.isArray(result?.releasedListings) ? result.releasedListings : [];
  return [result?.listing, ...releasedListings].filter(Boolean);
};

export const getCheckInErrorMessage = error =>
  error?.message ||
  error?.error?.message ||
  error?.statusText ||
  'Check-in failed. Please try again.';

const checkInSortTime = checkIn =>
  Date.parse(checkIn?.createdAt || checkIn?.startTime || checkIn?.checkedInAt || 0) || 0;

export const getUpdatedCurrentUserPublicMoorageState = (currentUser, result) => {
  const checkIn = result?.checkIn;
  if (!currentUser || !checkIn) return currentUser;

  const isOwnVesselCheckIn = checkIn.type !== CHECK_IN_TYPE_REPORTED_VESSEL;
  const releasedCheckIns = Array.isArray(result?.releasedCheckIns) ? result.releasedCheckIns : [];
  const closedPreviousCheckIns = Array.isArray(result?.closedPreviousCheckIns)
    ? result.closedPreviousCheckIns
    : [];
  const attributes = currentUser.attributes || {};
  const profile = attributes.profile || {};
  const privateData = profile.privateData || {};
  const existingHistory = Array.isArray(privateData[CHECK_IN_HISTORY_PRIVATE_DATA_KEY])
    ? privateData[CHECK_IN_HISTORY_PRIVATE_DATA_KEY]
    : [];
  const nextHistoryByKey = new Map();

  [...existingHistory, ...releasedCheckIns, ...closedPreviousCheckIns, checkIn].forEach(item => {
    if (!item) return;
    const key = item.id || `${item.userId || 'user'}:${item.listingId || 'listing'}`;
    nextHistoryByKey.set(key, item);
  });

  return {
    ...currentUser,
    attributes: {
      ...attributes,
      profile: {
        ...profile,
        privateData: {
          ...privateData,
          [CHECK_IN_PRIVATE_DATA_KEY]: isOwnVesselCheckIn
            ? checkIn
            : privateData[CHECK_IN_PRIVATE_DATA_KEY] || null,
          [CHECK_IN_HISTORY_PRIVATE_DATA_KEY]: Array.from(nextHistoryByKey.values()).sort(
            (a, b) => checkInSortTime(b) - checkInSortTime(a)
          ),
        },
      },
    },
  };
};

export const getRefetchedCurrentUserPublicMoorageState = (currentUser, activeCheckIn, history) => {
  if (!currentUser) return currentUser;

  const attributes = currentUser.attributes || {};
  const profile = attributes.profile || {};
  const privateData = profile.privateData || {};
  const nextHistory = Array.isArray(history) ? history : [];

  return {
    ...currentUser,
    attributes: {
      ...attributes,
      profile: {
        ...profile,
        privateData: {
          ...privateData,
          [CHECK_IN_PRIVATE_DATA_KEY]: activeCheckIn || null,
          [CHECK_IN_HISTORY_PRIVATE_DATA_KEY]: nextHistory,
        },
      },
    },
  };
};

export const refreshCurrentUserPublicMoorageState = async (currentUser, dispatch) => {
  if (!currentUser) return currentUser;

  const [activeResponse, historyResponse] = await Promise.all([
    fetchActivePublicMoorageCheckInForCurrentUser(),
    fetchPublicMoorageCheckInHistory(),
  ]);
  const activeCheckIn = activeResponse?.result?.checkIn || null;
  const history = Array.isArray(historyResponse?.result?.checkIns)
    ? historyResponse.result.checkIns
    : [];
  const updatedCurrentUser = getRefetchedCurrentUserPublicMoorageState(
    currentUser,
    activeCheckIn,
    history
  );

  dispatch(currentUserShowSuccess(updatedCurrentUser));
  return updatedCurrentUser;
};

const PublicMoorageCheckInModal = props => {
  const {
    id = 'PublicMoorageCheckInModal',
    isOpen,
    onClose,
    onCheckInSuccess,
    listing,
    mode: initialMode = 'form',
    checkIn: activeCheckIn,
  } = props;
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const config = useConfiguration();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(currentUserSelector);
  const boatDetails = getUserBoatDetails(currentUser);
  const ownVesselName = boatDetails?.boatName || boatDetails?.name || null;
  const ownVesselId = boatDetails?.vessel || boatDetails?.vesselId || boatDetails?.id || null;

  const [mode, setMode] = useState(initialMode);
  const [durationType, setDurationType] = useState('tonight');
  const [selectedCheckInType, setSelectedCheckInType] = useState(
    boatDetails ? CHECK_IN_TYPE_OWN_VESSEL : CHECK_IN_TYPE_REPORTED_VESSEL
  );
  const [reportedVesselName, setReportedVesselName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedCheckIn, setConfirmedCheckIn] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const onManageDisableScrolling = (componentId, disableScrolling) => {
    dispatch(manageDisableScrolling(componentId, disableScrolling));
  };

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setConfirmedCheckIn(null);
      setSubmitError(null);
      setDurationType('tonight');
      setSelectedCheckInType(
        boatDetails ? CHECK_IN_TYPE_OWN_VESSEL : CHECK_IN_TYPE_REPORTED_VESSEL
      );
      setReportedVesselName('');
    }
  }, [isOpen, initialMode, boatDetails]);

  const checkInData = useMemo(() => {
    if (mode === 'confirmation' && confirmedCheckIn) {
      return getCheckInDetails(confirmedCheckIn, listing);
    }
    if (mode === 'details' && activeCheckIn) {
      return getCheckInDetails(activeCheckIn, listing);
    }
    return null;
  }, [mode, confirmedCheckIn, activeCheckIn, listing]);

  const checkInUserId =
    mode === 'confirmation' ? confirmedCheckIn?.userId : activeCheckIn?.userId || null;
  const isCurrentUserCheckIn = checkInUserId === currentUser?.id?.uuid;

  const isReportingAnotherVessel = selectedCheckInType === CHECK_IN_TYPE_REPORTED_VESSEL;
  const ownVesselMeta = [
    boatDetails?.length ? `${boatDetails.length} ft` : null,
    [boatDetails?.make, boatDetails?.model]
      .filter(Boolean)
      .join(' ')
      .trim() || null,
  ]
    .filter(Boolean)
    .join(' · ');

  const handleConfirm = async () => {
    if (!listing?.id?.uuid) return;

    if (!isAuthenticated) {
      const from = `${location.pathname}${location.search}`;
      const loginPath = createResourceLocatorString('LoginPage', routeConfiguration(), {}, {});
      history.push({ pathname: loginPath, state: { from } });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const bypassGeofence = isCheckInGeofenceBypassEnabled();
      const userCoordinates = bypassGeofence ? null : await getBrowserCoordinates();
      validateCheckInGeofence({ listing, userCoordinates });
      const response = await createCheckIn({
        listingId: listing.id.uuid,
        type: selectedCheckInType,
        durationType,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(selectedCheckInType === CHECK_IN_TYPE_OWN_VESSEL && ownVesselId
          ? { vesselId: ownVesselId }
          : {}),
        ...(selectedCheckInType === CHECK_IN_TYPE_OWN_VESSEL && ownVesselName
          ? { boatName: ownVesselName }
          : {}),
        ...(isReportingAnotherVessel && reportedVesselName.trim()
          ? { reportedVesselName: reportedVesselName.trim() }
          : {}),
        ...(userCoordinates
          ? {
              userLatitude: userCoordinates.latitude,
              userLongitude: userCoordinates.longitude,
            }
          : {}),
      });
      const result = response?.result || response || {};
      const newCheckIn = result?.checkIn;
      const autoExpiredPreviousCheckIn =
        result?.autoExpiredPreviousCheckIn ||
        (Array.isArray(result?.closedPreviousCheckIns) && result.closedPreviousCheckIns.length > 0);

      const listingsToStore = getCheckInListingsToStore(result);
      if (listingsToStore.length > 0) {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };
        const data = listingsToStore.length === 1 ? listingsToStore[0] : listingsToStore;
        dispatch(addMarketplaceEntities({ data: { data } }, sanitizeConfig));
      }

      if (autoExpiredPreviousCheckIn) {
        onTriggerInfo('Your previous check-in was ended automatically.');
      }

      const updatedCurrentUser = getUpdatedCurrentUserPublicMoorageState(currentUser, result);
      if (updatedCurrentUser !== currentUser) {
        dispatch(currentUserShowSuccess(updatedCurrentUser));
      }

      setConfirmedCheckIn(newCheckIn);
      setMode('confirmation');
      refreshCurrentUserPublicMoorageState(updatedCurrentUser, dispatch).catch(refreshError => {
        console.error('Failed to refetch public moorage check-in state:', refreshError);
      });
      try {
        onCheckInSuccess?.(result);
      } catch (callbackError) {
        console.error('Failed to update public moorage check-in state:', callbackError);
      }
    } catch (error) {
      const message = getCheckInErrorMessage(error);
      setSubmitError(message);
      onTriggerError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateToListingDetails = e => {
    onRequestClose(e);
    const listingId = listing?.id?.uuid;
    if (!listingId) return;
    const slug = createSlug(listing?.attributes?.title || 'public-moorage');
    history.push(
      createResourceLocatorString('ListingPage', routeConfiguration(), { id: listingId, slug }, {})
    );
  };

  const onRequestClose = e => {
    if (isSubmitting) return;
    onClose?.(e);
  };

  const stopPropagation = event => {
    event.stopPropagation();
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={onRequestClose}
      usePortal
      containerClassName={css.modalContainer}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      {mode === 'form' ? (
        <div className={css.modalContent} onClick={stopPropagation} onMouseDown={stopPropagation}>
          <div className={css.modalHeader}>
            <div className={css.modalTitle}>Check In</div>
            <div className={css.modalSubtitle}>{listing?.attributes?.title || ''}</div>
          </div>
          <div className={css.modalBody}>
            <div className={css.section}>
              <div className={css.sectionTitle}>Who's at this moorage?</div>
              {boatDetails ? (
                <button
                  type="button"
                  className={classNames(css.boatOption, {
                    [css.boatOptionSelected]: selectedCheckInType === CHECK_IN_TYPE_OWN_VESSEL,
                  })}
                  onClick={() => setSelectedCheckInType(CHECK_IN_TYPE_OWN_VESSEL)}
                >
                  <span className={css.optionRadio} />
                  <span className={css.optionText}>
                    <span className={css.boatName}>
                      {ownVesselName || 'My boat'} <span className={css.optionNote}>— my boat</span>
                    </span>
                    {ownVesselMeta ? <span className={css.boatMeta}>{ownVesselMeta}</span> : null}
                  </span>
                </button>
              ) : (
                <>
                  <NamedLink name="MyBoatPage" className={css.boatLink}>
                    Add My Boat info to check in for your own boat
                  </NamedLink>
                </>
              )}
              <button
                type="button"
                className={classNames(css.boatOption, {
                  [css.boatOptionSelected]: selectedCheckInType === CHECK_IN_TYPE_REPORTED_VESSEL,
                })}
                onClick={() => setSelectedCheckInType(CHECK_IN_TYPE_REPORTED_VESSEL)}
              >
                <span className={css.optionRadio} />
                <span className={css.optionText}>
                  <span className={css.boatName}>
                    Another boat{' '}
                    <span className={css.optionNote}>— I'm reporting someone else here</span>
                  </span>
                </span>
              </button>
              {isReportingAnotherVessel ? (
                <label className={css.reportedVesselField}>
                  <span className={css.inputLabel}>Boat name (optional)</span>
                  <input
                    type="text"
                    className={css.boatInput}
                    placeholder="Type the boat name if you know it"
                    value={reportedVesselName}
                    onChange={e => setReportedVesselName(e.target.value)}
                  />
                </label>
              ) : null}
            </div>

            <div className={css.section}>
              <div className={css.sectionTitle}>Show as occupied for...</div>
              <div className={css.durationOptions}>
                {durationOptions.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={classNames(css.durationOption, {
                      [css.durationOptionSelected]: durationType === option.key,
                    })}
                    onClick={() => setDurationType(option.key)}
                  >
                    <div className={css.durationLabel}>{option.label}</div>
                    <div className={css.durationHelper}>{option.helper}</div>
                  </button>
                ))}
              </div>
              <div className={css.notice}>
                <div className={css.noticeTitle}>Heads up:</div>
                <div className={css.noticeBody}>
                  {isReportingAnotherVessel
                    ? "You're reporting another vessel for community visibility. This doesn't replace any required harbormaster check-in or fee payment — local rules still apply."
                    : "Check-ins show the community a moorage is occupied. They don't replace any required harbormaster check-in or fee payment — local rules still apply."}
                </div>
              </div>
            </div>

            {submitError ? (
              <div className={css.errorMessage} role="alert">
                {submitError}
              </div>
            ) : null}

            <PrimaryButton
              className={css.confirmButton}
              onClick={handleConfirm}
              inProgress={isSubmitting}
              disabled={isSubmitting}
            >
              Confirm check in
            </PrimaryButton>
            <SecondaryButton
              className={css.cancelButton}
              onClick={onRequestClose}
              disabled={isSubmitting}
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      ) : (
        <div className={css.modalBody} onClick={stopPropagation} onMouseDown={stopPropagation}>
          <div className={css.confirmHeader}>
            <div className={css.confirmIcon}>
              <Icons name="checkCircle" />
            </div>
            <div className={css.confirmTitle}>
              {isCurrentUserCheckIn ? "You're Checked In!" : 'Checked In'}
            </div>
            <div className={css.confirmSubtitle}>
              {isCurrentUserCheckIn
                ? 'Other boaters can now see this moorage as occupied.'
                : 'This moorage is currently marked as occupied.'}
            </div>
            <div className={css.confirmSubtitle}>
              Reminder: If this location requires fees or in-person check-in, please contact the
              harbormaster directly.
            </div>
          </div>

          <div className={css.confirmDetails}>
            <div className={css.detailRow}>
              <span className={css.detailLabel}>Location</span>
              <span className={css.detailValue}>{checkInData?.location}</span>
            </div>
            {checkInData?.boatName ? (
              <div className={css.detailRow}>
                <span className={css.detailLabel}>Boat</span>
                <span className={css.detailValue}>{checkInData.boatName}</span>
              </div>
            ) : null}
            <div className={css.detailRow}>
              <span className={css.detailLabel}>Expires</span>
              <span className={css.detailValue}>
                {formatCheckout(intl, checkInData?.expires, checkInData?.timeZone)}
              </span>
            </div>
          </div>

          <PrimaryButton className={css.confirmButton} onClick={onRequestClose}>
            Done
          </PrimaryButton>
          <SecondaryButton className={css.cancelButton} onClick={navigateToListingDetails}>
            View Details
          </SecondaryButton>
        </div>
      )}
    </Modal>
  );
};

export default PublicMoorageCheckInModal;
